import React, { useEffect, useState } from 'react'
import { X, Download, Share, PlusSquare } from 'lucide-react'
import { getInstallPrompt, promptInstall, isStandalone, isIOS } from '../pwaInstall'

/**
 * Installatiebanner (PWA, 2026-09-10): verschijnt op telefoons/tablets (< 1024 px) na inloggen,
 * onderaan het scherm. Android/Chrome: knop "Installeren" (native prompt). iPhone/iPad: uitleg
 * Deel → Zet op beginscherm. "Later" verbergt hem 14 dagen. Niet tonen als de app al
 * geïnstalleerd is of zolang de cookiebanner in beeld staat.
 */
const KEY = 'ugc4you_install_banner_dismissed_at'
const DAYS = 14

export default function InstallBanner() {
  const [visible, setVisible] = useState(false)
  const [mode, setMode] = useState(null) // 'android' | 'ios'

  useEffect(() => {
    if (typeof window === 'undefined' || isStandalone()) return undefined
    if (window.innerWidth >= 1024) return undefined
    try { const t = Number(localStorage.getItem(KEY) || 0); if (t && Date.now() - t < DAYS * 24 * 60 * 60 * 1000) return undefined } catch { /* genegeerd */ }

    let timer = null
    let cancelled = false
    const decide = () => {
      if (cancelled) return
      if (document.querySelector('.cb-inner')) { timer = setTimeout(decide, 2000); return } // cookiebanner eerst
      if (isIOS()) { setMode('ios'); setVisible(true); return } // iOS heeft nooit een install-prompt
      if (getInstallPrompt()) { setMode('android'); setVisible(true); return }
      // andere browsers zonder prompt: wacht nog even op beforeinstallprompt
    }
    timer = setTimeout(decide, 2500)
    const onInstallable = () => { if (!cancelled && !visible && !isIOS()) { setMode('android'); setVisible(true) } }
    const onInstalled = () => { setVisible(false) }
    window.addEventListener('ugc-installable', onInstallable)
    window.addEventListener('ugc-installed', onInstalled)
    return () => { cancelled = true; if (timer) clearTimeout(timer); window.removeEventListener('ugc-installable', onInstallable); window.removeEventListener('ugc-installed', onInstalled) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dismiss = () => { setVisible(false); try { localStorage.setItem(KEY, String(Date.now())) } catch { /* genegeerd */ } }
  const install = async () => { const outcome = await promptInstall(); if (outcome === 'accepted') setVisible(false); else dismiss() }

  if (!visible) return null

  return (
    <div role="dialog" aria-label="UGC4you als app installeren" className="lg:hidden fixed inset-x-3 bottom-3 z-[65] rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_12px_40px_-12px_rgba(10,15,13,0.25)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      <button type="button" onClick={dismiss} aria-label="Sluiten" className="absolute top-2 right-2 p-2.5 rounded-lg text-[#94A3B8] hover:text-[#0A0F0D] hover:bg-gray-50">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <img src="/icons/icon-192.png" alt="" className="w-11 h-11 rounded-xl border border-[#E5E7EB] flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-bold text-[#0A0F0D] text-[15px] leading-tight">Zet UGC4you op je telefoon</div>
          {mode === 'android' ? (
            <p className="text-sm text-[#475569] mt-1 leading-snug">Als app op je beginscherm, met meldingen bij nieuwe berichten en voorstellen.</p>
          ) : (
            <ol className="text-sm text-[#475569] mt-1.5 space-y-1.5 leading-snug">
              <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full border border-[#E5E7EB] text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span><span>Tik onderin Safari op <Share className="inline w-4 h-4 text-[#3EB489] -mt-0.5" /> <strong>Deel</strong></span></li>
              <li className="flex items-center gap-2"><span className="w-5 h-5 rounded-full border border-[#E5E7EB] text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span><span>Kies <PlusSquare className="inline w-4 h-4 text-[#3EB489] -mt-0.5" /> <strong>Zet op beginscherm</strong></span></li>
            </ol>
          )}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        {mode === 'android' ? (
          <button type="button" onClick={install} className="flex-1 inline-flex items-center justify-center gap-2 min-h-[44px] rounded-full bg-[#3EB489] hover:bg-[#359E78] text-white font-bold text-sm shadow-[0_4px_14px_-3px_rgba(62,180,137,0.5)]">
            <Download className="w-4 h-4" /> Installeren
          </button>
        ) : (
          <button type="button" onClick={dismiss} className="flex-1 inline-flex items-center justify-center min-h-[44px] rounded-full bg-[#3EB489] hover:bg-[#359E78] text-white font-bold text-sm">Begrepen</button>
        )}
        <button type="button" onClick={dismiss} className="px-4 min-h-[44px] rounded-full border border-[#E5E7EB] text-sm font-semibold text-[#475569] hover:bg-gray-50">Later</button>
      </div>
    </div>
  )
}
